import { Avatar, Box, Chip, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { checkForEmptyNotes, parsePersonColValue,formatAssigneeValue } from "../../helper/helpersSubModule";
import { applyStylestoCellRenderer, extractValueForShareableRollupList } from "../../helper/helperFunctions";
import "./CustomDropdownRenderer.css"; // Import the external CSS file
import PropTypes from 'prop-types';
import xTablesStore from "../../redux/xTablesStore";


export const CustomDropdownRenderer = ({ params, updateDB, isSelectionType, updateListData, globalStyle, isEditRowPopup, readOnlyMode, type}) => {
  const [usedInitials, setUsedInitials] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const memoizedUsedInitials = useMemo(() => usedInitials, [usedInitials]);
  let { node, column, eGridCell, colDef, data } = params;
  let {isFilterOrSummaryViewActive} = xTablesStore.getState().gridReducer;

  useEffect(() => {
    const checkFocus = () => {
      const focusedCell = params?.api?.getFocusedCell();
  
      setIsFocused(
        focusedCell &&
        focusedCell.rowIndex === params.node.rowIndex &&
        focusedCell?.column?.getColId() === params?.column?.getColId()
      );
    };
  
    checkFocus();
  
    params?.api?.addEventListener('cellFocused', checkFocus);
  
    return () => {
      params?.api?.removeEventListener('cellFocused', checkFocus);
    };
  }, [params]);
  

  const getColumnValue = (columnType, rawValue, memoizedUsedInitials) => {
    if (rawValue === "" || rawValue === null || rawValue === undefined || rawValue === "[]" ) return [];

    if (columnType === "shareableRollupList") {
      return rawValue !== '' ? [extractValueForShareableRollupList(rawValue)] : [];
    }

    try {
      if (["person", "Assignee"].includes(columnType)) {
        const formattedValue = formatAssigneeValue(rawValue);
        const value = JSON.parse(formattedValue);
    
        if (params.colDef?.listData) {
          const listData = JSON.parse(params?.colDef?.listData);
          const hasMatch = listData.some(ld => value.some(v => v.email === ld.email));
    
          if (hasMatch) {
            return parsePersonColValue(formattedValue, memoizedUsedInitials, columnType);
          } else {
            updateDB(JSON.stringify([]));
            return [];
          }
        } else {
          return parsePersonColValue(formattedValue, memoizedUsedInitials, columnType);
        }
      }
    } catch (error) {
      // If formattedValue is undefined or parsing fails, fall back to rawValue
      return parsePersonColValue(rawValue, memoizedUsedInitials, columnType);
    }

    if (columnType === "dropdown") {
      return parsePersonColValue(rawValue, memoizedUsedInitials, columnType);
    }

    return [rawValue];
  };

  const columnType = colDef?.type;
  const value = getColumnValue(columnType, params?.value, memoizedUsedInitials);
  let columnName = colDef.field + "_es";
  let cellFormatting = data ? data[columnName]?.cellStyle : {};
  let cellFormat = data ? data[columnName]?.format : '';
  const getInitialsFormatting = (cellFormatting) => {
    let defaultFontSize = 8;
    let defaultHeight = 15;
    let defaultWidth = 20;

    if (cellFormatting?.fontSize) {
      const parsedFontSize = parseFloat(cellFormatting.fontSize.replace('px', '')) - 3;
      const calculatedHeight = parsedFontSize * 1.8;
      const calculatedWidth = calculatedHeight + 4.6;

      return {
        fontSize: `${parsedFontSize}px`,
        height: `${calculatedHeight}px`,
        width: `${calculatedWidth}px`
      };
    }

    return {
      fontSize: `${defaultFontSize}px`,
      height: `${defaultHeight}px`,
      width: `${defaultWidth}px`
    };
  };
  
  const handleEditing = () => {
  params.api.startEditingCell({
      rowIndex: node.rowIndex,
      colKey: column
    });
  };

  const cellStyles = applyStylestoCellRenderer(cellFormat);
  const { fontSize: avatarFontSize, height: avatarHeight, width: avatarWidth } = getInitialsFormatting(cellFormatting);
  const formatItem = (item, node) => {
    let originalValue= typeof params?.value === 'object' ? params?.value: typeof params?.value === 'string' && params?.value.startsWith('{')? JSON.parse(params?.value):params?.value
    if (node?.childrenAfterGroup?.length || isFilterOrSummaryViewActive || (type && type == "card")) {
     
      if (item.includes("=>")) {
        const [left, right] = item.split("=>").map(part => part.trim());

        const parsePart = (part) => {
          const splitIndex = part.indexOf(": ");
          if (splitIndex === -1) return { label: '', value: part.trim() };

          const label = part.slice(0, splitIndex).trim();
          const value = part.slice(splitIndex + 2).trim();

          return { label, value };
        };
  
        const leftPart = parsePart(left);
        const rightPart = parsePart(right);
        const sameLabel = leftPart.label && leftPart.label === rightPart.label;

        return (
          <>
            {sameLabel ? (
              <>
                {leftPart.label}<strong>: </strong>{leftPart.value}
                <strong> ⇒ </strong>{rightPart.value}
              </>
            ) : (
              <>
                {leftPart.label}<strong>: </strong>{leftPart.value}
                <strong> ⇒ </strong>{rightPart.label}<strong>: </strong>{rightPart.value}
              </>
            )}
          </>
        );
      } else if (item.includes(": ")) {
        const [label, value] = item.split(": ").map(p => p.trim());
        const opacityStyle = label === "Default Status" ? {fontStyle: 'italic' } : {};
        return (
          <div style={opacityStyle}>
            {label}<strong>: </strong>{value}
          </div>
        );
      }
    }
    const opacityStyle = originalValue?.rowId === "None" ? { fontStyle: 'italic' } : {};
    return (
      <div  style={opacityStyle}>
        {item}
      </div>
    );
  };
 
  return (
    <Box
      className={`dropdown-renderer-box ${isEditRowPopup ? 'edit-popup' : ''}`}
      style={{ height: value ? "fit-content" : `${eGridCell?.clientHeight - 4}px` }}
    >
      <Stack
        direction={"row"}
        className="dropdown-renderer-stack"
      >
        {value?.map((item, index) => {
          if (((colDef.type === "person" || colDef.type === "Assignee") && item?.username) || ((colDef.type !== "person" && colDef.type !== "Assignee") && item)) {
            return (
              <Chip
                key={index}
                label={
                  colDef.type === 'person' || colDef.type === "Assignee" ? (
                    <Stack direction="row" alignItems="center" spacing={1} className="chip-stack">
                      <div className="chip-content">
                        <Avatar
                          className="chip-avatar"
                          style={{
                            fontSize: avatarFontSize,
                            width: avatarWidth,
                            height: avatarHeight,
                          }}
                        >
                          <span
                            style={{ ...cellStyles, fontSize: avatarFontSize }}
                            className="no-text-decoration"
                          >
                            {item?.initials}
                          </span>
                        </Avatar>
                        <Typography
                          variant="body2"
                          className="chip-username"
                          style={{
                            ...cellStyles,
                            background: cellFormatting?.backgroundColor || "transparent",
                            fontSize: cellFormatting?.fontSize || "11px",
                            color: cellFormatting?.color || "black",
                          }}
                        >
                          {item?.username}
                        </Typography>
                      </div>
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      style={{
                        ...cellStyles,
                        background: cellFormatting?.backgroundColor || "transparent",
                        fontSize: cellFormatting?.fontSize || "11px",
                        color: cellFormatting?.color || "black",
                        fontFamily: cellFormatting?.fontFamily || "Arial",
                        lineHeight: "1",
                        whiteSpace: 'normal',       // allow text to wrap
                        wordBreak: 'break-word',    // wrap long words
                        overflowWrap: 'break-word',
                      }}
                    >
                      {(colDef.type === 'shareableRollupList') && typeof item === "string"
                        ?
                        formatItem(item, node)
                        : item}

                    </Typography>
                  )
                }
                size="small"
                sx={{
                  background: cellFormatting?.backgroundColor || "transparent",
                  fontSize: cellFormatting?.fontSize || "12px",
                  color: cellFormatting?.color || "black",
                  fontFamily: cellFormatting?.fontFamily || "Arial",
                  fontWeight: cellFormat || '',
                  border: "2px solid #bdbdbd",
                  borderLeft: `2px solid ${isSelectionType === "single" ? "#0871a4" : "#6adc99"}`,
                  borderRight: `2px solid ${isSelectionType === "single" ? "#0871a4" : "#6adc99"}`,
                  borderRadius: "1rem",
                  height: "fit-content",
                  padding: 0,
                  '& .css-1pjtbja': {
                    padding: colDef.type === 'person' || colDef.type === "Assignee" ? '0px 2px !important' : '0px 8px'
                  },
                  '& .MuiChip-label': {
                    padding: '0px 2px', 
                  },
                }}
              />
            );
          }
          return null;
        })}
      </Stack>

      {isFocused &&
        !params?.node?.data?.isAzureRow &&
        <ArrowDropDownIcon
          className={`dropdown-opener-icon ${isEditRowPopup ? 'visible' : (colDef.type === 'shareableRollupList' &&
            params.node?.childrenAfterGroup?.length > 0 && !colDef?.azureColumn) ? 'status-parent' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleEditing();
          }}
        />}

      {params?.colDef?.field && params?.data?.hasOwnProperty(params?.colDef?.field + '_es') &&
        params?.data[params?.colDef?.field + "_es"] !== undefined &&  params.colDef.editable!==false &&
        !checkForEmptyNotes(params?.data[params.colDef.field + "_es"]?.notes) && (
          <ArrowDropDownIcon
            className="notes-icon"
            onClick={(e) => { params.onClickOpenRightDrawer("notes", 0); }} />
        )}
    </Box>
  );
};
CustomDropdownRenderer.propTypes = {
  params: PropTypes.shape({
      value: PropTypes.any, // Supports multiple types
      node: PropTypes.object, // ag-Grid row node
      column: PropTypes.object, // ag-Grid column object
      eGridCell: PropTypes.object,
      onClickOpenRightDrawer: PropTypes.func, // Cell DOM element
      colDef: PropTypes.shape({
          type: PropTypes.string.isRequired, // Ensures 'type' is a required string
      }).isRequired,
      data: PropTypes.object, // Row data
  }).isRequired,
  updateDB: PropTypes.func, // Function to update the database
  isSelectionType: PropTypes.any, // Boolean flag for selection type
  updateListData: PropTypes.func, // Function to update list data
  globalStyle: PropTypes.object, // Object for global styles
  isEditRowPopup: PropTypes.bool, // Boolean flag for edit row popup
  readOnlyMode: PropTypes.bool, 
  type:PropTypes.string
};

// ✅ Adding defaultProps to prevent undefined errors
CustomDropdownRenderer.defaultProps = {
  updateDB: () => {},
  isSelectionType: false,
  updateListData: () => {},
  globalStyle: {},
  isEditRowPopup: false,
};
